'use strict';

/**
 * Association chip decoration — two coordinated behaviours for the association UIs (the inline
 * RecordSet-AssociationEditor tabs on read views + their add-pickers), so an admin can tell
 * similarly-named records apart by a short identifier code next to the name.
 *
 *  (A) DEFAULT identifier chip per entity. Every association side shows its natural human-facing code
 *      beside the Name (Material → Material Code, Pay Item → Item Code, …). Applied by setting each
 *      side's `ChipFields`, which drives BOTH surfaces at once — the inline current-associations list
 *      (RecordSet-AssociationManager.composeChips) AND the add-picker (EntityTags) — but only where the
 *      side has no curated ChipFields already, so hand-tuned associations are left alone.
 *
 *  (B) USER-SELECTABLE decoration on the current-associations LIST. The add-picker already lets a user
 *      pin extra columns as chips (pict-section-picker's field-decoration ⚙, persisted per entity in
 *      localStorage). We extend that SAME choice to the editor's current-associations rows by wrapping
 *      the manager's `listAssociatedRecords`: it builds each row carrying the full `OtherRecord`, and we
 *      append a chip for each user-pinned field. So one ⚙ choice decorates both the picker and the list.
 *
 * App-local integration — no node_module edits. Applied once, after the associations are registered.
 */

// Per-entity default identifier chip: the natural short, human-facing code that differentiates same-named
// records (chosen from each entity's Meadow schema). Entities with no suitable identifier — BidItem, Lot,
// Test, TestSpecification — are intentionally absent (they get no default chip).
const ENTITY_CHIP_FIELDS =
{
	Material: { Field: 'MaterialCode', Label: 'Code' },
	PayItem: { Field: 'ItemCode', Label: 'Item' },
	LineItem: { Field: 'ItemNumber', Label: 'Item' },
	Equipment: { Field: 'Code', Label: 'Code' },
	MixSpecification: { Field: 'Code', Label: 'Code' },
	Organization: { Field: 'VendorCode', Label: 'Vendor' },
	Contact: { Field: 'Email', Label: 'Email' },
	FundingSource: { Field: 'FundingSourceAccountCode', Label: 'Acct' },
	Lab: { Field: 'ExternalLabID', Label: 'Lab ID' },
	Project: { Field: 'ExternalProjectID', Label: 'Proj ID' },
	ReportNamedInstance: { Field: 'Hash', Label: 'Hash' },
	Sample: { Field: 'LabControlNumber', Label: 'Ctrl #' },
	TestInstance: { Field: 'SampleTestNumber', Label: 'Test #' },
	TestSpecificationSet: { Field: 'Year', Label: 'Year' },
};

// Mirrors pict-section-picker's field-decoration storage (client-only, keyed per entity).
const DECORATION_KEY_PREFIX = 'PictSectionPicker.Decoration.';

function _chipFieldName(pSpec) { return (typeof pSpec === 'string') ? pSpec : ((pSpec && pSpec.Field) || ''); }

/**
 * (A) Give each association side a default identifier ChipFields entry where it has none. Drives both the
 * inline list chips and the add-picker's EntityTags. Preserves any side that already has curated chips.
 * @param {any} pManager - the RecordSetAssociationManager provider.
 */
function applyDefaultAssociationChips(pManager)
{
	if (!pManager || !pManager.associations) { return; }
	Object.keys(pManager.associations).forEach((pHash) =>
	{
		const tmpAssoc = pManager.associations[pHash];
		[ 'SideA', 'SideB' ].forEach((pSideKey) =>
		{
			const tmpSide = tmpAssoc && tmpAssoc[pSideKey];
			if (!tmpSide || !tmpSide.Entity) { return; }
			if (Array.isArray(tmpSide.ChipFields) && (tmpSide.ChipFields.length > 0)) { return; }   // keep curated chips
			const tmpChip = ENTITY_CHIP_FIELDS[tmpSide.Entity];
			if (tmpChip) { tmpSide.ChipFields = [ { Field: tmpChip.Field, Label: tmpChip.Label } ]; }
		});
	});
}

/** Read the user's picker-pinned decoration field list for an entity (client-only localStorage; safe). */
function _readDecorationFields(pEntity)
{
	if (!pEntity || (typeof window !== 'object') || !window || (typeof window.localStorage !== 'object') || !window.localStorage) { return []; }
	try
	{
		const tmpRaw = window.localStorage.getItem(`${DECORATION_KEY_PREFIX}${pEntity}`);
		const tmpParsed = tmpRaw ? JSON.parse(tmpRaw) : [];
		return Array.isArray(tmpParsed) ? tmpParsed.filter((pField) => ((typeof pField === 'string') && pField)) : [];
	}
	catch (pError) { return []; }
}

/**
 * (B) Wrap listAssociatedRecords so the current-associations rows also show the user's ⚙-pinned columns
 * (the same per-entity field-decoration choice the add-picker uses). Idempotent; best-effort.
 * @param {any} pManager - the RecordSetAssociationManager provider.
 */
function patchAssociationListDecoration(pManager)
{
	if (!pManager || pManager.__assocListDecorationPatched) { return; }
	if (typeof pManager.listAssociatedRecords !== 'function') { return; }
	pManager.__assocListDecorationPatched = true;
	const fOriginal = pManager.listAssociatedRecords.bind(pManager);
	pManager.listAssociatedRecords = function (pAssociationHash, pThisRecordSetName, pThisID)
	{
		return Promise.resolve(fOriginal(pAssociationHash, pThisRecordSetName, pThisID)).then((pRows) =>
		{
			try
			{
				if (!Array.isArray(pRows) || (pRows.length === 0)) { return pRows; }
				const tmpSides = this.resolveSides(pAssociationHash, pThisRecordSetName);
				const tmpEntity = tmpSides && tmpSides.otherSide && tmpSides.otherSide.Entity;
				const tmpDecoFields = _readDecorationFields(tmpEntity);
				if (!tmpEntity || (tmpDecoFields.length === 0)) { return pRows; }
				// Don't repeat a field already shown as the default / curated chip.
				const tmpExisting = new Set(((tmpSides.otherSide.ChipFields) || []).map(_chipFieldName).filter(Boolean));
				pRows.forEach((pRow) =>
				{
					const tmpRecord = pRow && pRow.OtherRecord;
					if (!tmpRecord) { return; }
					tmpDecoFields.forEach((pField) =>
					{
						if (tmpExisting.has(pField)) { return; }
						const tmpValue = tmpRecord[pField];
						if ((tmpValue === undefined) || (tmpValue === null) || (tmpValue === '')) { return; }
						pRow.Chips = (Array.isArray(pRow.Chips) ? pRow.Chips : []).concat([ { Text: `${pField}: ${tmpValue}` } ]);
					});
				});
			}
			catch (pError) { /* decoration is best-effort — never break the associations list */ }
			return pRows;
		});
	};
}

module.exports = { ENTITY_CHIP_FIELDS, applyDefaultAssociationChips, patchAssociationListDecoration };
